import React, { useState, useEffect, useRef } from 'react';
import { X, Crown, Club, Heart, Spade, Diamond, Sparkles, Coffee, LayoutGrid, CheckCircle2, Target, Megaphone, BrainCircuit, AlertTriangle, Lightbulb, Zap, Lock, ExternalLink, Eye, ChevronLeft, ChevronRight, MousePointer2, Building2, Layout } from 'lucide-react';
import './App.css';

const App = () => {
    const [activeCardId, setActiveCardId] = useState(null);
    const [hoveredCardId, setHoveredCardId] = useState(null);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showLightbox, setShowLightbox] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [isCardFlipped, setIsCardFlipped] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [activeDeck, setActiveDeck] = useState('content'); // 'content' or 'realestate'
    
    // Touch handling for swipe
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const isSwiping = useRef(false);

    // Track window resize for responsive calculations
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowWidth < 768;

    // --- Configuration ---
    const logoUrl = "/images/logo.png";
    
    // Background images for different screen orientations
    const landscapeBgUrl = "/images/bg-landscape.png";
    const portraitBgUrl = "/images/bg-portrait.png";

    // --- Data ---
    const services = [
        {
            id: 1,
            category: 'media',
            title: "Blind Stealing",
            hebrewSubtitle: "תכנים שמחברים למותג",
            cardDescription: "מהלך שיווקי מבוסס AI אוקיאנוס כחול",
            rank: "A",
            suit: "♠",
            suitIcon: <Spade className="w-full h-full fill-current" />,
            suitColor: "text-gray-900",
            suitColorLight: "text-gray-600",
            customHeader: "- 10 ימי עסקים ואנחנו באוויר",
            customSubHeader: "(בהתאם למסמך הדרישות)",
            description: [
                "יצירת נכס דיגיטלי לא ממותג ולא מוזהה עם המותג",
                "הגדרת יועץ AI בתוך התוכן להכוונה, עזרה, המלצה לרכש ולמידה",
                "קידום התכנים במגוון אתרים גדולים ונישות עיקריות מקושרות למותג",
                "הבנת נקודות הכאב, הדילמות והצרכים של קהל היעד על בסיס איסוף מידע באמצעות ה - AI",
                "ובהמשך לכך יצירת מגוון תכנים נוספים תחת אותו התחום מבוססי שאלות אמיתיות של קהלי היעד",
                "מיפוי הקהל לפי משך זמן צריכת התוכן ( מי שקרא 2 שניות, 5 שניות, 30 שניות)",
                "קמפיין המרה ייעודי לצורכי התוכן על בסיס רמות עניין",
                "ביסוס נראות המותג במקביל באתרי התוכן , החדשות, האפליקציות והנישות המתאימות לחיזוק המותג והמכר"
            ],
            bottomNote: "מתאים לתחומים בועטים ותחרותיים, שיכולים לרכב על תחומי עניין חזקים ודומיננטים ולסחוף אליהם אהדה וחיבור.",
            chipsValue: "50K",
            showcaseImage: "/images/blind-stealing-showcase.png",
            showcaseButtonText: "צפה בדוגמה ויזואלית"
        },
        {
            id: 2,
            category: 'media',
            title: "Over - Card Media",
            hebrewSubtitle: "קלף חזק יותר של קהלים - מבוסס חשיפה",
            cardDescription: "קמפיין דאטה צד ג' מפולח לפי קטגוריות מבוקשות",
            rank: "K",
            suit: "♥",
            suitIcon: <Heart className="w-full h-full fill-current" />,
            suitColor: "text-red-600",
            suitColorLight: "text-red-400",
            introText: "קמפיין אפיון קבוצות קהלים, חשיפתי חד וחזק לעד חמישה סגמנטים לבחירת הלקוח מתוך רשימה של קהלים לקנייה וטירגוט, על בסיס רכש חכם וקהלים מתוך קטגוריות שונות.",
            audienceExamples: [
                "רכבי יוקרה", "שעוני יוקרה", "טבעוניים", "שוק ההון", "משקיעים",
                "תזונה קטוגנית", "נדלן בישראל", "נדלן בחול", "נדלן יוקרה", 
                "דירות להשקעה", "חובבי מזון", "חובבי יין"
            ],
            closingText: "הקמפיין משלב שזירה של קהלים בעלי מאפיינים רלוונטיים לקהל המטרה והגשה חכמה בכלים מתקדמים מבוססי AI.",
            features: [
                { icon: <Megaphone size={16} />, label: "כלי חשיפה", value: "וידאו, מודעות \\ באנרים, מעברונים וכלי הגשה מתקדמים באתרים מובילים ובנישות.", className: "md:col-span-2" },
                { icon: <CheckCircle2 size={16} />, label: "כלול במחיר", value: "רכש הקהלים, טרגוט והגשה.", className: "md:col-span-1" },
                { icon: <Target size={16} />, label: "המלצה", value: "גיוון מסרים לכל קהל והפרדה לסגמנטים ללמידה ורכישה עתידית.", className: "md:col-span-1" }
            ],
            bottomNote: "עלות הפעילות לפי CPM של 43 שקלים ורפטיציה של עד 8 חשיפות לגולש.",
            chipsValue: "100K",
            showcaseImage: "/images/showcase.png",
            showcaseButtonText: "צפה בדוגמה ויזואלית"
        },
        {
            id: 3,
            category: 'media',
            title: "Double or Nothing",
            hebrewSubtitle: "קמפיין מדויק ואלגנטי",
            cardDescription: "ייעודי לפרסונות ואינדיבידואלים שאנחנו רוצים למותג",
            rank: "Q",
            suit: "♣",
            suitIcon: <Club className="w-full h-full fill-current" />,
            suitColor: "text-gray-900",
            suitColorLight: "text-gray-600",
            showcaseImage: "/images/double-or-nothing-showcase.png",
            showcaseButtonText: "צפה בדוגמה ויזואלית",
            narrativeFlow: [
                {
                    icon: <BrainCircuit size={18} className="text-emerald-400" />,
                    title: "דיוק ופילוח AI",
                    text: `באמצעות אלגוריתם AI ייחודי למערכת גוגל DV360, אנחנו מצליחים להגיע לעוד ועוד "פרסונות" שרמת ההיכרות והפילוח שלהן היא בין הגבוהות המתאפשרות כיום בעולמות ה-AI.
על בסיס גישה לאלפי בורסות פרסום בעולם, אינסוף שילובים ואתרים שבהם ביקרו, תוכן שצרכו, התכתבויות מייל שקיימו (בהתאם לכל תנאי הפרטיות הקיימים), עמודי נחיתה שעליהם הקליקו או השאירו פרטים (הבדל מהותי), אתרים קבועים בהם הם מבקרים או אתרים חדשים, הזמנות שבוצעו ועוד.
כל אלה נאספים אודות הגולש ובונים לכל פרט ופרט פרופיל מדויק ככל שאפשר.`
                },
                {
                    icon: <AlertTriangle size={18} className="text-yellow-400" />,
                    title: "מינוף הדאטה",
                    text: `כולם מדברים על דאטה ועל הערך שלה. אבל הערך של הדאטה קיים רק כאשר אפשר למנף אותו.
נכון, כולנו יכולים לעשות קמפיין לקהל דומה - ואז הוא נהיה דומה לדומים ומתרחק אט אט מקו הקהל שרצינו.`
                },
                {
                    icon: <Lightbulb size={18} className="text-yellow-200" />,
                    title: "האסטרטגיה",
                    text: `רק קמפיין Double or Nothing יכול להגיע ללקוחות שלך, כפרסונות לא ככלל, בדרך חכמה ואלגנטית.
להעיר אותם לרגע ולהגיע בצורה הכי חדה אליהם - ולכל מי שמרכיבי הפרסונה שלו זהים במהות ובאופן הפעולה והבחירה שלהם.`
                },
                {
                    icon: <Zap size={18} className="text-orange-400" />,
                    title: "פנייה שמניעה לפעולה",
                    text: `אם אתם יודעים מי הקהל שלכם - בואו נדבר איתו.
נגיע לקהל עם סרטונים, באנרים וכלי פרימיום. נשב לרגע את תשומת ליבו בין כל הפרסומים האחרים סביבו, ונעביר לו מסר.
נניע אותו לפעולה, נציע לו הצעה שאי אפשר לסרב לה. מספיק לנו שהגולש יקליק - והוא שלנו.`
                }
            ],
            bottomNote: "מטרת הקמפיין: יצירת כמות חשיפות עצומה לקהל היעד, תוך ייעוד המטרה של הקמפיין על בסיס הקלקות לכדי צביעה והוספת הגולש/פרסונה לדאטה שלנו בנכסים הדיגיטליים שלכם.",
            chipsValue: "75K"
        },
        {
            id: 4,
            category: 'realestate',
            title: "On the Button",
            hebrewSubtitle: "B2B \\ Real estate campaign",
            cardDescription: "אינטגרציה שמייצרת לידים, חוקרת אותם ומתעדפת אותם",
            rank: "J",
            suit: "♦",
            suitIcon: <Diamond className="w-full h-full fill-current" />,
            suitColor: "text-red-600",
            suitColorLight: "text-red-400",
            customHeader: "B2B \\ Real estate On the button campaign",
            introText: "קמפיין מדיה רחב, שמטרתו איסוף לידים אל תוך עמוד נחיתה מבוסס AI שיחקור ויתעדף את הלידים בתוך מערכות ה CRM ללקוח לפי מגוון פרמטרים ודיוקים, לדוגמה: כתובת מייל עסקי, איסוף מידע מעמוד הלינקדין ועוד.",
            features: [
                { icon: <Megaphone size={16} />, label: "חשיפה", value: "העלאת קמפיין מבוסס באנרים וידאו רחב במדיה." },
                { icon: <BrainCircuit size={16} />, label: "אוטומציה", value: "הפנייה לעמוד נחיתה ייעודי מבוסס AI והתממשקות ל CRM" },
                { icon: <Target size={16} />, label: "תיעדוף", value: "תיעדוף הלידים לסוכן המכירות" }
            ],
            layoutStyle: "horizontal",
            chipsValue: "25K"
        },
        {
            id: 5,
            category: 'realestate',
            title: "Real Estate",
            hebrewSubtitle: "Backdoor Campaign",
            cardDescription: "קמפיין נדל\"ן לקהל סגור",
            rank: "10",
            suit: "♠",
            suitIcon: <Spade className="w-full h-full fill-current" />,
            suitColor: "text-gray-900",
            suitColorLight: "text-gray-600",
            customHeader: "Real estate Backdoor campaign",
            introText: "קמפיין מדיה ייעודי בפלטפורמה סגורה THE LIST, המותאם רק לנכסים ספציפיים או לסיטואציות בהם הדחף למכור הוא גבוה ויש צורך למימוש מיידי (דירות שענטז, מכירה לצורך מימון בנקאי, דירה אחרונה).",
            features: [
                { icon: <Lock size={16} />, label: "קהילה סגורה", value: "העלאת נכס נדלני במחיר טוב וכדאי לקהל משקיעים קהילה סגורה." },
                { icon: <Sparkles size={16} />, label: "השבחה", value: "יצירת mini-site יעודי לנכס כולל הדמויות AI עם סגנונות עיצוב שונים." }
            ],
            layoutStyle: "horizontal",
            chipsValue: "MIN",
            demoLink: "https://seaside-lease.emergent.host/",
            ctaText: "לצפייה בנכס לדוגמה"
        }
    ];

    // Filter services by active deck
    const filteredServices = services.filter(s => 
        activeDeck === 'content' ? s.category === 'media' : s.category === 'realestate'
    );
    
    // Reset card index when deck changes
    useEffect(() => {
        setCurrentCardIndex(activeDeck === 'content' ? 1 : 0);
    }, [activeDeck]);

    const activeService = services.find(s => s.id === activeCardId);

    const SmartLogo = ({ src, className, light = false }) => {
        const [error, setError] = useState(false);
        if (error) {
            return (
                <div className={`flex flex-col items-center ${className}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <Crown size={20} className={light ? "text-yellow-400" : "text-[#b39656]"} />
                    </div>
                    <h1 className={`font-poker tracking-wider uppercase text-center leading-none ${light ? "text-white" : "text-black"}`}>
                        <span className="text-xl block mb-1">Reach</span>
                        <span className={`text-sm block font-sans font-light tracking-[0.3em] ${light ? "text-gray-300" : "text-gray-600"}`}>New Media</span>
                    </h1>
                </div>
            );
        }
        return (
            <div className="glass-logo-container px-3 py-2 md:px-4 md:py-2.5 rounded-lg border border-yellow-500/20 bg-black/30 backdrop-blur-sm shadow-lg">
                <img src={src} alt="Reach New Media" className={`${className} object-contain`} onError={() => setError(true)} />
            </div>
        );
    };

    // Mobile Card Header Component
    const MobileCardHeader = ({ service }) => (
        <div className="flex items-center gap-3 p-4 bg-white/10 rounded-xl border border-white/20 mb-4">
            <div className={`w-14 h-14 bg-white rounded-lg flex items-center justify-center shadow-lg ${service.suitColor}`}>
                <div className="text-center">
                    <span className="font-poker font-bold text-lg block leading-none">{service.rank}</span>
                    <div className="w-4 h-4 mx-auto">{service.suitIcon}</div>
                </div>
            </div>
            <div className="flex-1 text-right">
                <h2 className={`font-poker font-bold text-lg uppercase ${service.suitColorLight || 'text-white'}`}>
                    {service.title}
                </h2>
                <p className="text-gray-300 text-sm font-sans whitespace-pre-line">{service.hebrewSubtitle}</p>
            </div>
        </div>
    );

    return (
        <div className="poker-table-bg min-h-screen relative font-sans text-slate-200 overflow-hidden select-none flex flex-col justify-between" dir="rtl">
            {/* Dynamic Background Image */}
            <div 
                className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-all duration-500"
                style={{
                    backgroundImage: `url(${isMobile ? portraitBgUrl : landscapeBgUrl})`
                }}
            />
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 z-[1] bg-black/40" />
            
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@400;700;900&family=Heebo:wght@300;400;700&family=Amatic+SC:wght@400;700&display=swap');
                .font-poker { font-family: 'Roboto Slab', serif; }
                .font-sans { font-family: 'Heebo', sans-serif; }
                .poker-table-bg { position: relative; }
                .glass-panel { background: rgba(16, 24, 30, 0.95); backdrop-filter: blur(16px); border: 1px solid rgba(255, 255, 255, 0.1); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5); }
                .card-transition { transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1); }
                .info-window { background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1); backdrop-filter: blur(4px); transition: all 0.3s ease; }
                .info-window:hover { background: rgba(255, 255, 255, 0.08); border-color: rgba(255, 255, 255, 0.25); }
                .audience-tag { background: rgba(234, 179, 8, 0.15); border: 1px solid rgba(234, 179, 8, 0.4); color: #fcd34d; padding: 2px 8px; border-radius: 99px; font-size: 0.75rem; transition: all 0.2s; }
                .custom-scroll::-webkit-scrollbar { width: 4px; }
                .custom-scroll::-webkit-scrollbar-thumb { background: rgba(234, 179, 8, 0.3); border-radius: 10px; }
            `}</style>

            <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm z-30 transition-opacity duration-500 pointer-events-none ${activeCardId || showInfoModal ? 'opacity-100' : 'opacity-0'}`}></div>

            {/* Buy-In Button (TOP RIGHT) */}
            <div className={`absolute top-3 right-3 md:top-8 md:right-8 z-50 transition-all duration-500 ${!activeCardId ? 'opacity-100' : 'opacity-0 pointer-events-none -translate-y-10'}`}>
                <button onClick={() => setShowInfoModal(true)} className="bg-gradient-to-r from-black/50 via-amber-900/30 to-black/50 hover:from-black/60 hover:via-amber-800/40 hover:to-black/60 backdrop-blur-sm text-white font-sans py-2 px-3 md:py-2 md:px-4 rounded-full shadow-sm flex items-center gap-2 transition-all hover:scale-105 active:scale-95 border border-amber-500/20" data-testid="buy-in-button">
                    <div className="w-8 h-8 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-red-600 to-red-700 border-2 border-dashed border-white/60 flex items-center justify-center text-white">
                        <span className="text-xs md:text-sm font-black">50K</span>
                    </div>
                    <span className="tracking-wider text-xs md:text-sm uppercase font-medium">BUY IN</span>
                </button>
            </div>

            {/* --- Info Modal (Buy In) --- */}
            {showInfoModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowInfoModal(false)}>
                    <div className="relative w-full max-w-md glass-panel text-white p-6 md:p-8 rounded-xl border border-yellow-500/20 animate-fade-in-up" onClick={(e) => e.stopPropagation()} data-testid="info-modal">
                        <button onClick={() => setShowInfoModal(false)} className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors" data-testid="close-modal-button"><X size={24} /></button>
                        
                        <div className="space-y-6 text-center pt-6">
                            <div className="bg-white/5 p-4 rounded-lg border border-white/10 shadow-inner">
                                <p className="font-bold text-yellow-400 text-xl md:text-2xl">תקציב מינימום לקמפיין: 50K</p>
                            </div>
                            
                            <p className="text-gray-300 text-sm md:text-base leading-relaxed px-2 border-t border-white/5 pt-4">
                                עלויות נוספות כרוכות בהתאם להגדרות ולצרכים מבוססי בנית נכסים דיגיטלים והגדרות AI לבוטים.
                            </p>
                        </div>

                        <button onClick={() => setShowInfoModal(false)} className="mt-6 md:mt-8 w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 rounded shadow-lg transition-colors font-sans tracking-wider uppercase text-sm md:text-base" data-testid="confirm-modal-button">מצוין, בוא נתחיל</button>
                    </div>
                </div>
            )}

            {/* --- Lightbox for Showcase Images --- */}
            {showLightbox && lightboxImage && (
                <div 
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-fade-in-up" 
                    onClick={() => setShowLightbox(false)}
                    data-testid="lightbox-modal"
                >
                    <button 
                        onClick={() => setShowLightbox(false)} 
                        className="absolute top-4 right-4 md:top-8 md:right-8 p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-10"
                        data-testid="close-lightbox-button"
                    >
                        <X size={24} />
                    </button>
                    
                    <div className="relative max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
                        <img 
                            src={lightboxImage} 
                            alt="דוגמה ויזואלית" 
                            className="w-full h-full object-contain rounded-lg shadow-2xl border border-white/10"
                        />
                    </div>
                    
                    <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm font-sans">
                        לחץ בכל מקום לסגירה
                    </p>
                </div>
            )}

            {/* --- Stage Area (Active Card) - DIFFERENT LAYOUT FOR MOBILE --- */}
            <div className={`fixed inset-0 flex items-center justify-center z-40 transition-all duration-500 ${activeCardId ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}>
                {activeService && (
                    <>
                        {/* MOBILE LAYOUT - Full screen panel with cream theme */}
                        {isMobile ? (
                            <div 
                                className="w-full h-full flex flex-col" 
                                style={{ background: 'linear-gradient(145deg, #f5f0e8 0%, #ebe4d8 50%, #dfd5c5 100%)' }}
                                data-testid="active-card-view-mobile"
                            >
                                {/* Top bar with close button */}
                                <div className="flex-none h-2 w-full bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800"></div>
                                <div className="flex-none p-3 flex justify-start items-center">
                                    <button 
                                        onClick={() => setActiveCardId(null)} 
                                        className="p-2 text-gray-600 hover:text-gray-900 bg-white/50 hover:bg-white/80 rounded-full transition-all shadow-sm"
                                        data-testid="close-card-button"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                                
                                {/* Scrollable content */}
                                <div className="flex-1 overflow-y-auto px-4 pb-6 custom-scroll-cream">
                                    {/* Mobile Card Header - Centered */}
                                    <div className={`flex flex-col items-center text-center p-3 rounded-xl border mb-4 bg-white/80 border-amber-200`}>
                                        <div className={`w-14 h-14 bg-white rounded-lg flex items-center justify-center shadow-md border mb-2 border-amber-200 ${activeService.suitColor}`}>
                                            <div className="text-center">
                                                <span className="font-poker font-bold text-lg block leading-none">{activeService.rank}</span>
                                                <div className="w-4 h-4 mx-auto">{activeService.suitIcon}</div>
                                            </div>
                                        </div>
                                        <h2 className="font-poker font-bold text-lg uppercase text-gray-900">
                                            {activeService.title}
                                        </h2>
                                        <p className="text-gray-700 text-sm font-sans whitespace-pre-line">{activeService.hebrewSubtitle}</p>
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="space-y-3">
                                        {/* Show customHeader only for card 1 (which has customSubHeader) */}
                                        {activeService.customHeader && activeService.customSubHeader && (
                                            <div className="text-center mb-3 pb-2 border-b border-amber-900/20">
                                                <h3 className="text-base font-sans font-bold tracking-wide text-gray-900">{activeService.customHeader}</h3>
                                                <span className="text-xs text-amber-800/70 font-sans">{activeService.customSubHeader}</span>
                                            </div>
                                        )}

                                        {Array.isArray(activeService.description) && (
                                            <div className="space-y-2">
                                                {activeService.description.map((item, idx) => (
                                                    <div key={idx} className="bg-white/80 p-2.5 rounded-xl border border-amber-200/60 flex items-start gap-2">
                                                        <div className="mt-1 w-2 h-2 rounded-full flex-shrink-0 bg-gray-800"></div>
                                                        <p className="text-[13px] leading-snug font-sans text-gray-800 text-right">{item}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {activeService.narrativeFlow && (
                                            <div className="space-y-2">
                                                {activeService.narrativeFlow.map((section, idx) => (
                                                    <div key={idx} className="bg-white/80 p-3 rounded-xl border border-amber-200/60">
                                                        <div className="flex items-center gap-2 mb-1.5 border-b border-amber-200/60 pb-1.5 text-gray-900">
                                                            {section.icon}
                                                            <span className="text-xs font-bold font-sans">{section.title}</span>
                                                        </div>
                                                        <p className="text-[13px] leading-relaxed font-sans text-gray-700 text-right whitespace-pre-line">{section.text}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {activeService.introText && (
                                            <div className="bg-white/80 p-3 rounded-xl border border-amber-200/60">
                                                <p className="text-[13px] leading-snug font-sans text-gray-800 text-right">{activeService.introText}</p>
                                            </div>
                                        )}

                                        {activeService.audienceExamples && (
                                            <div className="bg-white/60 p-3 rounded-xl border border-amber-200/60">
                                                <p className="text-sm font-bold font-sans mb-2 text-gray-900">דוגמאות לקהלים:</p>
                                                <div className="flex flex-wrap gap-1.5 justify-start">
                                                    {activeService.audienceExamples.map((ex, i) => (
                                                        <span 
                                                            key={i} 
                                                            className="px-2 py-1 rounded-full text-[11px] font-sans font-semibold bg-amber-100 text-amber-900 border border-amber-300"
                                                        >
                                                            {ex}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {activeService.closingText && (
                                            <div className="p-2.5 rounded-xl border-r-4 bg-amber-100/70 border-amber-600">
                                                <p className="text-[13px] leading-snug font-sans text-gray-800 text-right">{activeService.closingText}</p>
                                            </div>
                                        )}

                                        {activeService.features && (
                                            <div className="space-y-2">
                                                {activeService.features.map((feat, i) => (
                                                    <div key={i} className="bg-white/80 p-2.5 rounded-xl border border-amber-200/60">
                                                        <div className="flex items-center gap-2 mb-1 pb-1 border-b border-amber-200/60 text-gray-800">
                                                            {feat.icon}
                                                            <span className="text-xs font-bold font-sans uppercase">{feat.label}</span>
                                                        </div>
                                                        <p className="text-[13px] text-gray-700 leading-snug">{feat.value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Action buttons */}
                                        <div className="pt-3 space-y-2.5 border-t border-amber-900/20">
                                            {activeService.showcaseImage && (
                                                <button 
                                                    onClick={() => {
                                                        setLightboxImage(activeService.showcaseImage);
                                                        setShowLightbox(true);
                                                    }} 
                                                    className="w-full flex items-center justify-center gap-2 font-bold py-2.5 px-4 rounded-lg shadow-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black"
                                                    data-testid="showcase-image-btn"
                                                >
                                                    <Eye size={18} />
                                                    <span className="font-sans text-sm">{activeService.showcaseButtonText}</span>
                                                </button>
                                            )}
                                            
                                            {activeService.demoLink && (
                                                <a 
                                                    href={activeService.demoLink} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="w-full flex items-center justify-center gap-2 font-semibold py-2.5 px-4 rounded-lg shadow-sm bg-amber-100 hover:bg-amber-200 border border-amber-400 text-amber-900"
                                                    data-testid="demo-link"
                                                >
                                                    <ExternalLink size={18} />
                                                    <span className="font-sans text-sm">{activeService.ctaText}</span>
                                                </a>
                                            )}
                                        </div>

                                        {activeService.bottomNote && (
                                            <div className="pt-2">
                                                <p className="text-sm text-center font-sans font-medium leading-snug px-3 py-2 rounded-lg text-amber-800 bg-amber-100/70">
                                                    {activeService.bottomNote}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* DESKTOP LAYOUT - Card + Panel with overlap */
                            <div className="w-full max-w-7xl flex flex-row items-center justify-center px-4" data-testid="active-card-view">
                                {/* The Flippable Card - positioned behind panel */}
                                <div 
                                    className={`relative flex-shrink-0 transition-all duration-700 ease-in-out z-10 ${isCardFlipped && activeService.showcaseImage ? 'w-[380px] h-[620px]' : 'w-[260px] h-[365px] transform -rotate-3'}`}
                                    style={{ perspective: '1000px', marginRight: '-80px' }}
                                >
                                    <div 
                                        className={`relative w-full h-full transition-transform duration-700 ease-in-out cursor-pointer`}
                                        style={{ 
                                            transformStyle: 'preserve-3d',
                                            transform: isCardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                                        }}
                                        onClick={() => activeService.showcaseImage && setIsCardFlipped(!isCardFlipped)}
                                    >
                                        {/* Front of Card */}
                                        <div 
                                            className="absolute inset-0 w-full h-full bg-white rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border-4 border-white overflow-hidden"
                                            style={{ backfaceVisibility: 'hidden' }}
                                        >
                                            <div className={`absolute top-3 left-3 flex flex-col items-center leading-none z-20 ${activeService.suitColor}`}>
                                                <span className="text-4xl font-poker font-bold">{activeService.rank}</span>
                                                <div className="w-6 h-6 mt-1">{activeService.suitIcon}</div>
                                            </div>
                                            <img 
                                                src={logoUrl} 
                                                alt="" 
                                                className="absolute inset-0 m-auto opacity-[0.08] max-h-[65%] max-w-[65%] object-contain pointer-events-none rotate-90"
                                            />
                                            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center z-10">
                                                <h2 className={`text-2xl font-poker font-bold uppercase tracking-wide mb-1.5 ${activeService.suitColor === 'text-red-600' ? 'text-red-700' : 'text-black'}`}>{activeService.title}</h2>
                                                <div className="w-10 h-0.5 bg-gray-400 mb-2"></div>
                                                {activeService.hebrewSubtitle && (
                                                    <h3 className="text-gray-800 font-bold text-sm whitespace-pre-line mb-1.5">{activeService.hebrewSubtitle}</h3>
                                                )}
                                                {activeService.cardDescription && (
                                                    <p className="text-gray-700 text-xs px-3">{activeService.cardDescription}</p>
                                                )}
                                            </div>
                                            <div className={`absolute bottom-3 right-3 flex flex-col items-center rotate-180 leading-none z-20 ${activeService.suitColor}`}>
                                                <span className="text-4xl font-poker font-bold">{activeService.rank}</span>
                                                <div className="w-6 h-6 mt-1">{activeService.suitIcon}</div>
                                            </div>
                                        </div>
                                        
                                        {/* Back of Card - Shows Image */}
                                        {activeService.showcaseImage && (
                                            <div 
                                                className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-xl shadow-[0_0_50px_rgba(234,179,8,0.3)] border-4 border-yellow-500/60 overflow-hidden flex items-center justify-center p-3"
                                                style={{ 
                                                    backfaceVisibility: 'hidden',
                                                    transform: 'rotateY(180deg)'
                                                }}
                                            >
                                                <img 
                                                    src={activeService.showcaseImage} 
                                                    alt="דוגמה ויזואלית"
                                                    className="w-full h-full object-contain rounded-lg shadow-xl"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Info Panel - Premium Modern Design - overlaps card */}
                                <div 
                                    className="relative w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-slideIn z-20"
                                    style={{ 
                                        background: 'linear-gradient(145deg, #f5f0e8 0%, #ebe4d8 50%, #dfd5c5 100%)',
                                        maxHeight: '90vh'
                                    }}
                                    data-testid="info-panel"
                                >
                                    {/* Decorative top border - always dark/amber */}
                                    <div className="h-2 w-full flex-shrink-0 bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800"></div>
                                    
                                    {/* Close button */}
                                    <button 
                                        onClick={() => { setActiveCardId(null); setIsCardFlipped(false); }} 
                                        className="absolute top-4 left-4 p-2.5 text-gray-600 hover:text-gray-900 rounded-full transition-all duration-300 z-50 hover:bg-black/10 hover:rotate-90" 
                                        data-testid="close-card-button"
                                    >
                                        <X size={22} />
                                    </button>
                                    
                                    <div className="p-4 lg:p-6 overflow-y-auto custom-scroll-cream flex-1">
                                        {/* Header */}
                                        {activeService.customHeader && (
                                            <div className="text-center mb-2 pb-2 border-b border-amber-900/20">
                                                <h3 className="text-lg lg:text-xl font-sans font-bold tracking-wide text-gray-900">
                                                    {activeService.customHeader}
                                                </h3>
                                                {activeService.customSubHeader && (
                                                    <p className="text-sm text-amber-800/70 font-sans mt-1">{activeService.customSubHeader}</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Description Grid */}
                                        {Array.isArray(activeService.description) && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                                                {activeService.description.map((item, idx) => (
                                                    <div 
                                                        key={idx} 
                                                        className="group bg-white/80 backdrop-blur-sm p-2.5 rounded-xl border border-amber-200/60 shadow-sm hover:shadow-lg hover:bg-white hover:border-amber-300 transition-all duration-300 hover:-translate-y-0.5 cursor-default"
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <div className="mt-1 w-2 h-2 rounded-full flex-shrink-0 transition-transform duration-300 group-hover:scale-125 bg-gray-800"></div>
                                                            <p className="text-[13px] leading-snug font-sans text-gray-800 text-right">{item}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Narrative Flow - 2 columns grid for better space usage */}
                                        {activeService.narrativeFlow && (
                                            <div className="grid grid-cols-2 gap-2 mb-3">
                                                {activeService.narrativeFlow.map((section, idx) => (
                                                    <div 
                                                        key={idx} 
                                                        className="group bg-white/80 backdrop-blur-sm p-2 rounded-xl border border-amber-200/60 shadow-sm hover:shadow-lg hover:bg-white transition-all duration-300"
                                                        style={{ animationDelay: `${idx * 100}ms` }}
                                                    >
                                                        <div className="flex items-center gap-1.5 mb-1 pb-1 border-b border-amber-200/60 text-gray-900">
                                                            <span className="transition-transform duration-300 group-hover:scale-110 flex-shrink-0">{section.icon}</span>
                                                            <span className="text-xs font-bold font-sans truncate">{section.title}</span>
                                                        </div>
                                                        <p className="text-[12px] leading-snug font-sans text-gray-700 text-right whitespace-pre-line">{section.text}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Intro Text - only for non-horizontal layout */}
                                        {activeService.introText && activeService.layoutStyle !== 'horizontal' && (
                                            <div className="bg-white/80 backdrop-blur-sm p-2.5 rounded-xl border border-amber-200/60 shadow-sm mb-2 hover:shadow-md transition-shadow duration-300">
                                                <p className="text-[12px] leading-snug font-sans text-gray-800 text-right">{activeService.introText}</p>
                                            </div>
                                        )}

                                        {/* Audience Examples */}
                                        {activeService.audienceExamples && (
                                            <div className="bg-white/60 backdrop-blur-sm p-3 rounded-xl border border-amber-200/60 mb-3">
                                                <p className="text-sm font-bold font-sans mb-2 text-gray-900">
                                                    דוגמאות לקהלים:
                                                </p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {activeService.audienceExamples.map((ex, i) => (
                                                        <span 
                                                            key={i} 
                                                            className="px-2.5 py-1 rounded-full text-xs font-sans font-semibold transition-all duration-300 hover:scale-105 cursor-default bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 hover:shadow-sm"
                                                            style={{ animationDelay: `${i * 50}ms` }}
                                                        >
                                                            {ex}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Closing Text */}
                                        {activeService.closingText && (
                                            <div className="p-2.5 rounded-xl mb-3 border-r-4 bg-amber-100/70 border-amber-600">
                                                <p className="text-[13px] leading-snug font-sans text-gray-800 text-right">{activeService.closingText}</p>
                                            </div>
                                        )}

                                        {/* Features Grid - Horizontal layout for Real Estate cards */}
                                        {activeService.features && activeService.layoutStyle === 'horizontal' ? (
                                            <div className="flex gap-4 mb-3">
                                                {/* Intro text on the right */}
                                                {activeService.introText && (
                                                    <div className="flex-1 bg-white/80 backdrop-blur-sm p-4 rounded-xl border border-amber-200/60 shadow-sm">
                                                        <p className="text-sm leading-relaxed font-sans text-gray-800 text-right">{activeService.introText}</p>
                                                    </div>
                                                )}
                                                {/* Features on the left */}
                                                <div className="flex-1 space-y-2">
                                                    {activeService.features.map((feat, i) => (
                                                        <div 
                                                            key={i} 
                                                            className="group bg-white/80 backdrop-blur-sm p-3 rounded-xl border border-amber-200/60 shadow-sm hover:shadow-lg hover:bg-white transition-all duration-300"
                                                        >
                                                            <div className="flex items-center gap-2 mb-1.5 pb-1.5 border-b border-amber-200/60 text-gray-800">
                                                                <span className="transition-transform duration-300 group-hover:scale-110">{feat.icon}</span>
                                                                <span className="text-sm font-bold font-sans uppercase tracking-wide">{feat.label}</span>
                                                            </div>
                                                            <p className="text-sm font-sans text-gray-700 leading-snug">{feat.value}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : activeService.features && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                                                {activeService.features.map((feat, i) => (
                                                    <div 
                                                        key={i} 
                                                        className={`group bg-white/80 backdrop-blur-sm p-2.5 rounded-xl border border-amber-200/60 shadow-sm hover:shadow-lg hover:bg-white transition-all duration-300 hover:-translate-y-0.5 ${feat.className || ''}`}
                                                    >
                                                        <div className="flex items-center gap-2 mb-1 pb-1 border-b border-amber-200/60 text-gray-800">
                                                            <span className="transition-transform duration-300 group-hover:scale-110">{feat.icon}</span>
                                                            <span className="text-xs font-bold font-sans uppercase tracking-wide">{feat.label}</span>
                                                        </div>
                                                        <p className="text-[13px] font-sans text-gray-700 leading-snug">{feat.value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Bottom section */}
                                        <div className="flex flex-col items-center gap-2 pt-2 border-t border-amber-900/20">
                                            {/* Showcase Image Button - flips the card */}
                                            {activeService.showcaseImage && (
                                                <button 
                                                    onClick={() => setIsCardFlipped(!isCardFlipped)} 
                                                    className="group flex items-center gap-2 bg-amber-100 hover:bg-amber-200 border border-amber-400 text-amber-900 font-semibold py-2 px-5 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md"
                                                    data-testid="showcase-image-btn-desktop"
                                                >
                                                    <Eye size={16} className="transition-transform duration-300 group-hover:scale-110" />
                                                    <span className="font-sans text-xs">{isCardFlipped ? 'חזור לקלף' : activeService.showcaseButtonText}</span>
                                                </button>
                                            )}
                                            
                                            {activeService.demoLink && (
                                                <a 
                                                    href={activeService.demoLink} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="group flex items-center gap-2 bg-amber-100 hover:bg-amber-200 border border-amber-400 text-amber-900 font-semibold py-2 px-5 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md"
                                                    data-testid="demo-link"
                                                >
                                                    <ExternalLink size={16} className="transition-transform duration-300 group-hover:rotate-12" />
                                                    <span className="font-sans text-xs">{activeService.ctaText}</span>
                                                </a>
                                            )}
                                            
                                            {activeService.bottomNote && (
                                                <p className="text-xs text-center max-w-2xl font-sans font-medium leading-snug px-2 py-1.5 rounded-lg text-amber-800 bg-amber-100/70">
                                                    {activeService.bottomNote}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            
            {/* Header / Hero */}
            <div className={`w-full flex flex-col items-center justify-start pt-12 md:pt-12 pb-0 md:pb-2 transition-all duration-500 z-10 flex-none relative ${activeCardId || showInfoModal ? 'opacity-0 scale-90' : 'opacity-100 scale-100'}`}>
                <div className="absolute top-3 left-3 md:top-5 md:left-5 z-20">
                    <SmartLogo src={logoUrl} light={true} className="h-6 md:h-10 w-auto drop-shadow-xl" />
                </div>
                <div className="text-center space-y-1 md:space-y-2 px-4 relative w-full max-w-4xl mx-auto mt-6 md:mt-2">
                    <div className="inline-flex items-center justify-center gap-2 px-4 md:px-5 py-2 md:py-2 rounded-full border border-yellow-500/30 bg-black/50 backdrop-blur-md shadow-lg" data-testid="hero-banner">
                        <div className="w-2 h-2 md:w-2 md:h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0"></div>
                        <span className="text-yellow-500 font-bold uppercase tracking-wider text-sm md:text-lg text-center leading-tight">2026 שנת AI | הכלים החדשים שלנו</span>
                    </div>
                    
                    {/* Dealer's Hint - Show on both mobile and desktop */}
                    <div className="flex items-center justify-center gap-1.5 mt-1 md:mt-2 animate-[pulse_2s_ease-in-out_infinite]">
                        <MousePointer2 size={14} className="text-yellow-400/80 flex-shrink-0" />
                        <span className="font-sans text-xs md:text-sm text-yellow-400/80">בחר את הזירה שלך כדי לחשוף את האסטרטגיה המנצחת</span>
                    </div>
                    
                    {/* Strategy Deck Switcher - Bold & Prominent */}
                    <div className="flex justify-center mt-1.5 md:mt-3">
                        <div 
                            className="inline-flex rounded-full bg-gradient-to-r from-gray-900/90 via-black/95 to-gray-900/90 backdrop-blur-xl p-1.5 md:p-1 border-2 border-yellow-400/50 shadow-[0_0_30px_rgba(234,179,8,0.3)]" 
                            data-testid="category-toggle"
                        >
                            <button
                                onClick={() => setActiveDeck('realestate')}
                                className={`px-5 md:px-8 py-3 md:py-3 rounded-full font-sans text-sm md:text-sm font-bold tracking-wider flex items-center gap-2 transition-all duration-300 ${
                                    activeDeck === 'realestate' 
                                        ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 text-black shadow-[0_0_20px_rgba(234,179,8,0.6)]' 
                                        : 'bg-transparent text-gray-300 hover:text-white hover:bg-white/10'
                                }`}
                                data-testid="category-realestate-btn"
                            >
                                <Building2 size={18} strokeWidth={2.5} />
                                <span>REAL ESTATE</span>
                            </button>
                            <button
                                onClick={() => setActiveDeck('content')}
                                className={`px-5 md:px-8 py-3 md:py-3 rounded-full font-sans text-sm md:text-sm font-bold tracking-wider flex items-center gap-2 transition-all duration-300 ${
                                    activeDeck === 'content' 
                                        ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 text-black shadow-[0_0_20px_rgba(234,179,8,0.6)]' 
                                        : 'bg-transparent text-gray-300 hover:text-white hover:bg-white/10'
                                }`}
                                data-testid="category-media-btn"
                            >
                                <Layout size={18} strokeWidth={2.5} />
                                <span>CONTENT & MEDIA</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* MOBILE: Card Fan Layout */}
            {isMobile && !activeCardId && (
                <div className="flex-1 flex flex-col items-center justify-end z-20 px-2 pb-4" data-testid="mobile-carousel">
                    
                    {/* Cards container - Fan style */}
                    <div 
                        className="relative w-full h-[340px] flex items-end justify-center"
                        onTouchStart={(e) => {
                            touchStartX.current = e.touches[0].clientX;
                            touchEndX.current = e.touches[0].clientX;
                            isSwiping.current = false;
                        }}
                        onTouchMove={(e) => {
                            touchEndX.current = e.touches[0].clientX;
                            if (Math.abs(touchStartX.current - touchEndX.current) > 10) {
                                isSwiping.current = true;
                            }
                        }}
                        onTouchEnd={() => {
                            const diff = touchStartX.current - touchEndX.current;
                            if (Math.abs(diff) > 50) {
                                if (diff > 0 && currentCardIndex < filteredServices.length - 1) {
                                    setCurrentCardIndex(prev => prev + 1);
                                } else if (diff < 0 && currentCardIndex > 0) {
                                    setCurrentCardIndex(prev => prev - 1);
                                }
                            }
                        }}
                    >
                        {/* All cards in fan layout */}
                        {filteredServices.map((service, index) => {
                            const offset = index - currentCardIndex;
                            const isActive = index === currentCardIndex;
                            const totalCards = filteredServices.length;
                            
                            // Fan layout calculations
                            const xOffset = offset * 80;
                            const rotation = offset * 8;
                            const translateY = Math.abs(offset) * 18;
                            const scale = isActive ? 1 : 0.85;
                            const zIndex = isActive ? 100 : 50 - Math.abs(offset) * 10;
                            const opacity = Math.abs(offset) > 1 ? 0 : 1;
                            
                            if (Math.abs(offset) > 1) return null;
                            
                            return (
                                <div 
                                    key={service.id}
                                    className="absolute bottom-0 cursor-pointer transition-all duration-400 ease-out origin-bottom"
                                    style={{
                                        width: '170px',
                                        height: '255px',
                                        transform: `translateX(${xOffset}px) translateY(${translateY}px) rotate(${rotation}deg) scale(${scale})`,
                                        zIndex,
                                        opacity,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isSwiping.current) {
                                            isSwiping.current = false;
                                            return;
                                        }
                                        if (isActive) {
                                            setActiveCardId(service.id);
                                        } else {
                                            setCurrentCardIndex(index);
                                        }
                                    }}
                                    data-testid={`carousel-card-${service.id}`}
                                >
                                    <div className={`w-full h-full bg-white rounded-xl shadow-2xl border-2 overflow-hidden relative transition-all duration-300 ${isActive ? 'border-yellow-400 shadow-[0_0_30px_rgba(234,179,8,0.5)]' : 'border-gray-300 shadow-[0_8px_25px_rgba(0,0,0,0.3)]'}`}>
                                        {/* Top left rank & suit */}
                                        <div className={`flex flex-col items-center absolute top-2 left-2 leading-none z-20 ${service.suitColor}`}>
                                            <span className="font-poker font-bold text-xl">{service.rank}</span>
                                            <div className="w-4 h-4">{service.suitIcon}</div>
                                        </div>
                                        
                                        {/* Center content */}
                                        <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center">
                                            <img 
                                                src={logoUrl} 
                                                alt="" 
                                                className="absolute opacity-[0.08] max-h-[75%] max-w-[75%] object-contain pointer-events-none rotate-90"
                                            />
                                            <h3 className={`font-poker font-bold text-sm uppercase leading-tight z-10 px-1 ${service.suitColor === 'text-red-600' ? 'text-red-700' : 'text-black'}`}>
                                                {service.title}
                                            </h3>
                                            <div className="w-8 h-0.5 bg-gray-400 my-1 z-10"></div>
                                            {service.hebrewSubtitle && (
                                                <p className="font-sans text-[9px] font-bold text-gray-800 z-10 whitespace-pre-line px-1 leading-tight">
                                                    {service.hebrewSubtitle}
                                                </p>
                                            )}
                                        </div>
                                        
                                        {/* Bottom right rank & suit (rotated) */}
                                        <div className={`flex flex-col items-center absolute bottom-2 right-2 rotate-180 leading-none z-20 ${service.suitColor}`}>
                                            <span className="font-poker font-bold text-xl">{service.rank}</span>
                                            <div className="w-4 h-4">{service.suitIcon}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Navigation - More prominent */}
                    <div className="flex items-center justify-center gap-3 mt-4 bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                        <button
                            onClick={() => currentCardIndex > 0 && setCurrentCardIndex(prev => prev - 1)}
                            className={`p-2 rounded-full transition-all ${currentCardIndex > 0 ? 'bg-yellow-500/80 text-black hover:bg-yellow-400' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
                            disabled={currentCardIndex === 0}
                        >
                            <ChevronRight size={18} />
                        </button>
                        
                        {/* Dots indicator */}
                        <div className="flex gap-2 px-2">
                            {filteredServices.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentCardIndex(idx)}
                                    className={`rounded-full transition-all ${idx === currentCardIndex ? 'bg-yellow-500 w-6 h-2.5' : 'bg-white/40 hover:bg-white/60 w-2.5 h-2.5'}`}
                                />
                            ))}
                        </div>
                        
                        <button
                            onClick={() => currentCardIndex < filteredServices.length - 1 && setCurrentCardIndex(prev => prev + 1)}
                            className={`p-2 rounded-full transition-all ${currentCardIndex < filteredServices.length - 1 ? 'bg-yellow-500/80 text-black hover:bg-yellow-400' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
                            disabled={currentCardIndex === filteredServices.length - 1}
                        >
                            <ChevronLeft size={18} />
                        </button>
                    </div>
                    
                    {/* Swipe hint */}
                    <p className="text-white/60 font-sans text-xs mt-2 text-center">
                        החלק או לחץ על הקלף
                    </p>
                </div>
            )}

            {/* DESKTOP: Cards Deck - Fan Layout */}
            {!isMobile && (
                <div className={`w-full flex-1 flex justify-center items-end pb-24 md:pb-32 overflow-visible z-20 perspective-1000 relative transition-all duration-700 ${activeCardId ? 'opacity-0 pointer-events-none translate-y-20' : 'opacity-100'}`} data-testid="cards-deck">
                    <div className="relative flex items-end justify-center w-full max-w-6xl h-48 md:h-60 lg:h-80">
                        {filteredServices.map((service, index) => {
                            const isHovered = hoveredCardId === service.id;
                            const totalCards = filteredServices.length;
                            
                            // Different layout for 2 cards vs 3 cards
                            let distanceFromCenter, xOffset, rotate, translateY;
                            
                            if (totalCards === 2) {
                                // For 2 cards: position them symmetrically with slight tilt
                                distanceFromCenter = index === 0 ? -0.5 : 0.5;
                                xOffset = 200;
                                rotate = index === 0 ? 5 : -5;
                                translateY = 0;
                            } else {
                                // For 3 cards: fan layout
                                distanceFromCenter = index - Math.floor(totalCards / 2);
                                xOffset = 130;
                                const rotateFactor = 4;
                                rotate = distanceFromCenter * rotateFactor;
                                translateY = Math.abs(distanceFromCenter) * 12;
                            }
                            
                            const zIndex = isHovered ? 100 : 10 + index;

                            return (
                                <div 
                                    key={service.id} 
                                    className="absolute bottom-0 w-40 h-60 lg:w-52 lg:h-80 origin-bottom" 
                                    style={{ 
                                        left: '50%', 
                                        marginLeft: windowWidth < 1024 ? '-5rem' : '-6.5rem', 
                                        transform: `translateX(${distanceFromCenter * xOffset}px) translateY(${translateY}px) rotate(${rotate}deg)`, 
                                        zIndex 
                                    }}
                                    onMouseEnter={() => !activeCardId && setHoveredCardId(service.id)}
                                    onMouseLeave={() => setHoveredCardId(null)}
                                    data-testid={`card-${service.id}`}
                                >
                                    <div className="absolute inset-0 w-full h-full z-0" />
                                    <div 
                                        className={`absolute inset-0 w-full h-full bg-white rounded-xl shadow-2xl border border-gray-300 cursor-pointer card-transition transform origin-bottom flex flex-col overflow-hidden group ${isHovered ? 'shadow-[0_0_50px_rgba(234,179,8,0.6)] border-yellow-400 -translate-y-8 scale-105' : ''}`} 
                                        onClick={() => setActiveCardId(service.id)}
                                    >
                                        <div className={`flex flex-col items-center absolute top-2 left-2 leading-none z-20 ${service.suitColor}`}>
                                            <span className="font-poker font-bold text-xl md:text-2xl">{service.rank}</span>
                                            <div className="w-4 h-4 md:w-5 md:h-5">{service.suitIcon}</div>
                                        </div>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center p-2 md:p-4 text-center">
                                            <img 
                                                src={logoUrl} 
                                                alt="" 
                                                className="absolute opacity-[0.08] max-h-[70%] max-w-[70%] object-contain pointer-events-none rotate-90"
                                            />
                                            <h3 className={`font-poker font-bold text-base md:text-lg lg:text-xl uppercase leading-tight z-10 ${service.suitColor === 'text-red-600' ? 'text-red-700' : 'text-black'}`}>{service.title}</h3>
                                            <div className="w-8 h-0.5 bg-gray-300 my-1 md:my-2 z-10"></div>
                                            <p className="font-sans text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-widest z-10 whitespace-pre-line">{service.hebrewSubtitle}</p>
                                            {service.cardDescription && (
                                                <p className="font-sans text-[9px] md:text-xs text-gray-600 z-10 mt-1 px-2">{service.cardDescription}</p>
                                            )}
                                        </div>
                                        <div className={`flex flex-col items-center absolute bottom-2 right-2 rotate-180 leading-none z-20 ${service.suitColor}`}>
                                            <span className="font-poker font-bold text-xl md:text-2xl">{service.rank}</span>
                                            <div className="w-4 h-4 md:w-5 md:h-5">{service.suitIcon}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;
